import React from "react";
import { useSettings } from "../context/SettingsContext";
import { alignClass } from "../lib/align";
import { Reveal, SectionHeading, DynamicIcon, Button, SmartImage } from "../components/ui";
import SEO from "../components/SEO";
import { Target, Eye, CheckCircle2 } from "lucide-react";

export default function About() {
  const { settings, content } = useSettings();
  const values = content.core_values || [];
  const why = content.why_choose || [];
  const process = content.process || [];

  return (
    <>
      <SEO title="About Us" description={content.about_intro} />
      <PageHero kicker="About Us" title="Building Spaces, Building Trust" subtitle={settings.tagline} />

      <section className="py-16 md:py-20 bg-white">
        <div className="container-x grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="relative rounded-xl2 overflow-hidden shadow-lift aspect-[4/3] border border-line">
              <SmartImage src={content.hero_image || settings.hero_image} alt="GNONS" w={900} />
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="kicker mb-3">Who We Are</div>
            <h2 className="text-3xl md:text-4xl font-bold text-ink leading-tight">Smart Design. Quality Execution.</h2>
            <p className={`mt-5 text-muted leading-relaxed ${alignClass(content.about_intro_align)}`}>{content.about_intro}</p>
            <div className="grid grid-cols-2 gap-4 mt-8">
              {(content.stats || []).map((s, i) => (
                <div key={i} className="rounded-xl border border-line p-4">
                  <div className="text-2xl font-extrabold text-gold">{s.value}</div>
                  <div className="text-xs text-muted mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-sand">
        <div className="container-x grid md:grid-cols-2 gap-6">
          <Reveal>
            <div className="rounded-xl2 bg-white border border-line p-8 h-full">
              <Target className="text-gold mb-4" size={30} />
              <h3 className="text-xl font-bold text-ink">Our Mission</h3>
              <p className={`mt-3 text-muted leading-relaxed ${alignClass(content.mission_align)}`}>{content.mission}</p>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="rounded-xl2 bg-white border border-line p-8 h-full">
              <Eye className="text-gold mb-4" size={30} />
              <h3 className="text-xl font-bold text-ink">Our Vision</h3>
              <p className={`mt-3 text-muted leading-relaxed ${alignClass(content.vision_align)}`}>{content.vision}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="container-x">
          <Reveal><SectionHeading center kicker="Core Values" title="What Drives Us" /></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
            {values.map((v, i) => (
              <Reveal key={i} delay={i * 60}>
                <div className="flex items-center gap-3 rounded-xl border border-line bg-sand p-5">
                  <CheckCircle2 className="text-gold shrink-0" size={20} />
                  <span className="font-semibold text-ink">{v}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-sand">
        <div className="container-x">
          <Reveal><SectionHeading center kicker="Why GNONS" title="Why Choose Us" /></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {why.map((w, i) => (
              <Reveal key={i} delay={i * 60}>
                <div className="flex gap-4 rounded-xl2 bg-white border border-line p-6 h-full">
                  <div className="w-12 h-12 shrink-0 rounded-full bg-gold/10 flex items-center justify-center"><DynamicIcon name={w.icon} className="text-gold" size={22} /></div>
                  <div><h3 className="font-bold text-ink">{w.title}</h3><p className="text-sm text-muted mt-1.5">{w.desc}</p></div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="container-x">
          <Reveal><SectionHeading center kicker="Working Process" title="How We Work" /></Reveal>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {process.map((p, i) => (
              <Reveal key={i} delay={i * 60}>
                <div className="rounded-xl2 border border-line p-6 h-full">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl font-extrabold text-gold">{p.step}</span>
                    <DynamicIcon name={p.icon} className="text-ink" size={22} />
                  </div>
                  <h3 className="font-bold text-ink">{p.title}</h3>
                  <p className="text-sm text-muted mt-1.5">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="text-center mt-12"><Button to="/contact" variant="primary">Get Free Consultation</Button></div>
        </div>
      </section>
    </>
  );
}

export function PageHero({ kicker, title, subtitle }) {
  return (
    <section className="pt-28 pb-8 md:pt-32 md:pb-12 bg-ink text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #C88A32 0, transparent 40%)" }} />
      <div className="container-x relative">
        <div className="kicker mb-3">{kicker}</div>
        <h1 className="text-3xl md:text-5xl font-extrabold leading-tight">{title}</h1>
        {subtitle && <p className="mt-3 text-white/60 max-w-xl text-base md:text-lg">{subtitle}</p>}
      </div>
    </section>
  );
}
